import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { savingsService } from '../../services/savingsService'

function Savings() {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createData, setCreateData] = useState({
    nba: '',
    kodeJenis: 'SUKARELA',
    setoranAwal: 0
  })
  const [types, setTypes] = useState([])

  useEffect(() => {
    fetchAccounts()
    fetchTypes()
  }, [page, search])

  const fetchAccounts = async () => {
    try {
      setLoading(true)
      const response = await savingsService.getAll({
        page,
        limit: 20,
        search
      })
      // Handle both: response.data.data (array) and response.data.data.data (nested)
      let accountsData = []
      if (Array.isArray(response.data?.data)) {
        accountsData = response.data.data
      } else if (Array.isArray(response.data?.data?.data)) {
        accountsData = response.data.data.data
      }
      setAccounts(accountsData)
      // Handle both meta structures
      const meta = response.data?.meta || response.data?.data?.meta
      setTotalPages(meta?.totalPages || 1)
    } catch (error) {
      console.error('Error fetching accounts:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTypes = async () => {
    try {
      const response = await savingsService.getTypes()
      setTypes(response.data.data || [])
    } catch (error) {
      console.error('Error fetching types:', error)
    }
  }

  const handleCreate = async () => {
    try {
      await savingsService.create(createData)
      alert('Rekening berhasil dibuat')
      setShowCreateModal(false)
      fetchAccounts()
      setCreateData({ nba: '', kodeJenis: 'SUKARELA', setoranAwal: 0 })
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal membuat rekening')
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID')
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Data Simpanan</h3>
        <div className="card-tools">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fas fa-plus mr-1"></i> Buka Rekening
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Cari nama atau no. rekening..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && setPage(1)}
              />
              <div className="input-group-append">
                <button className="btn btn-default" onClick={() => setPage(1)}>
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                <th>No. Rekening</th>
                <th>NBA</th>
                <th>Nama Anggota</th>
                <th>Jenis</th>
                <th>Saldo</th>
                <th>Tgl Buka</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Memuat data...
                  </td>
                </tr>
              ) : !Array.isArray(accounts) || accounts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">Tidak ada data</td>
                </tr>
              ) : (
                accounts.map((acc) => (
                  <tr key={acc.NoRekening}>
                    <td>{acc.NoRekening}</td>
                    <td>{acc.NBA}</td>
                    <td>{acc.NamaAnggota}</td>
                    <td>{acc.NamaJenis}</td>
                    <td className="text-right">{formatCurrency(acc.Saldo)}</td>
                    <td>{formatDate(acc.TglBuka)}</td>
                    <td>
                      <span className={`badge ${acc.Status === 'Aktif' ? 'badge-success' : 'badge-secondary'}`}>
                        {acc.Status}
                      </span>
                    </td>
                    <td>
                      <Link to={`/savings/${acc.NoRekening}`} className="btn btn-info btn-sm">
                        <i className="fas fa-eye"></i>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <div>Halaman {page} dari {totalPages}</div>
          <div>
            <button
              className="btn btn-default btn-sm mr-1"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              className="btn btn-default btn-sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Buka Rekening Simpanan</h5>
                <button type="button" className="close" onClick={() => setShowCreateModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>NBA Anggota *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={createData.nba}
                    onChange={(e) => setCreateData({ ...createData, nba: e.target.value })}
                    placeholder="Masukkan NBA"
                  />
                </div>
                <div className="form-group">
                  <label>Jenis Simpanan *</label>
                  <select
                    className="form-control"
                    value={createData.kodeJenis}
                    onChange={(e) => setCreateData({ ...createData, kodeJenis: e.target.value })}
                  >
                    {Array.isArray(types) && types.filter(t => t.KodeJenis === 'SUKARELA' || t.KodeJenis === 'BERJANGKA').map(t => (
                      <option key={t.KodeJenis} value={t.KodeJenis}>
                        {t.NamaJenis} (Min: Rp {t.SetoranMin.toLocaleString('id-ID')})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Setoran Awal *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={createData.setoranAwal}
                    onChange={(e) => setCreateData({ ...createData, setoranAwal: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Batal
                </button>
                <button type="button" className="btn btn-primary" onClick={handleCreate}>
                  Simpan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Savings
